import { NavLink } from 'react-router-dom';
import {
  MonitorPlay,
  ListChecks,
  Award,
  Quote,
  FlaskConical,
  Layers,
  ScrollText,
  BookOpen,
  Map,
} from 'lucide-react';

// The instructor's own words: "action buttons at the bottom of page on the
// left side seems cramped up and we should move them to the top bar so I can
// see them all the time without being lost." LeftNav.tsx used to answer eight
// destinations with one flex-1 icon row at the FOOT of the sidebar, labels
// only on hover (design/ace-chem-top-nav.html, design/Main.dc.html). This is
// the replacement: a second chrome row, always present, every destination
// named — never a tooltip-only icon.
//
// A plain horizontal-scroll row (no separate "narrow" branch) is deliberate:
// it is simultaneously the desktop row (Main.dc.html — everything fits, no
// scrollbar ever appears) and the phone/iPad row (Narrow.dc.html — the same
// markup overflows and scrolls, still fully labeled, 44px targets). One
// implementation, three states, nothing hidden behind a control that isn't
// itself reachable.

interface NavItem {
  to: string;
  /** Render a plain <a>: a static page outside the SPA router. */
  external?: boolean;
  label: string;
  Icon: typeof MonitorPlay;
  /** Icon colour. Carried over from the chips the old LeftNav footer used, so
   *  a destination keeps the colour the instructor already associates with it
   *  — the row is then scannable by colour as well as by word, which is what
   *  makes it usable at a glance mid-class. Applied to the ICON only: the
   *  label stays on the standard ink tokens so contrast is unaffected. */
  ink: string;
}

// The three destinations an instructor or student reaches every single day —
// design/Main.dc.html leads with these rather than burying them among tools.
const DAILY: NavItem[] = [
  { to: '/class', label: 'Class', Icon: MonitorPlay, ink: 'text-ws-danger' },
  { to: '/learner-plan', label: 'Learner plan', Icon: ListChecks, ink: 'text-ws-ai' },
  { to: '/me/record', label: 'My record', Icon: Award, ink: 'text-ws-success' },
  // Added when this bar replaced the LeftNav footer: the testimonial consent
  // form tells students they can narrow or revoke consent at
  // /me/testimonials, so the page has to be reachable from somewhere. It was
  // linked from the footer this bar deletes, and dropping it would re-break
  // the promise the form makes.
  { to: '/me/testimonials', label: 'My testimonials', Icon: Quote, ink: 'text-ws-accent-ink' },
];

// Study tools — the same four the old footer carried, just named now.
const TOOLS: NavItem[] = [
  { to: '/lab', label: 'Virtual Lab', Icon: FlaskConical, ink: 'text-ws-ai' },
  { to: '/flashcards', label: 'Flashcards', Icon: Layers, ink: 'text-ws-success' },
  { to: '/study', label: 'Study packs', Icon: ScrollText, ink: 'text-ws-warning' },
  { to: '/glossary', label: 'Glossary', Icon: BookOpen, ink: 'text-ws-accent-ink' },
  // Static pages under /guide, not React routes: they are self-contained
  // documents that also work offline and can be sent to a student as a file.
  // `external` makes the nav render a plain <a> so the SPA router does not try
  // to match them. Reachable by a guest — nothing behind them needs an account.
  { to: '/guide/', label: 'Course guide', Icon: Map, ink: 'text-ws-accent', external: true },
];

const LINK_BASE =
  'inline-flex h-11 lg:h-8 shrink-0 items-center gap-2 whitespace-nowrap rounded-md border border-transparent px-3 text-[13px] font-medium text-ws-ink-2 transition-colors hover:bg-ws-surface-2 hover:text-ws-ink';

function NavItemLink({ to, label, Icon, ink, external }: NavItem) {
  // A static page under /guide is not a React route. NavLink would ask the
  // router to match it, find nothing, and render the SPA's not-found instead
  // of the document.
  if (external) {
    return (
      <a href={to} className={LINK_BASE}>
        <Icon size={16} className={`shrink-0 ${ink}`} aria-hidden="true" />
        {label}
      </a>
    );
  }
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        `${LINK_BASE} ${
          isActive
            ? 'border-ws-border-strong bg-ws-accent-quiet font-semibold text-ws-accent-ink hover:bg-ws-accent-quiet hover:text-ws-accent-ink'
            : ''
        }`
      }
    >
      <Icon size={16} className={`shrink-0 ${ink}`} aria-hidden="true" />
      {label}
    </NavLink>
  );
}

export default function TopNav() {
  // Teach moved to TopBar (components/layout/TeachButton.tsx) at the
  // instructor's request — "move the orange Teach button to be beside Theme
  // button". This row is now destinations only, which is what it always
  // claimed to be.

  return (
    <nav
      aria-label="Primary"
      className="ws-scroll-thin flex items-center gap-1 overflow-x-auto border-b border-ws-nav-border bg-ws-nav px-2 sm:px-4 shrink-0"
    >
      {DAILY.map((item) => (
        <NavItemLink key={item.to} {...item} />
      ))}

      <span className="mx-1 h-6 w-px shrink-0 bg-ws-border" aria-hidden="true" />

      {TOOLS.map((item) => (
        <NavItemLink key={item.to} {...item} />
      ))}

    </nav>
  );
}
